import { ActionContext } from 'vuex';
import { NotificationsState, NotificationData, NotificationType, FCMMessagePayload } from './types';
import {
  getNotificationPreferences,
  isAlarmNotification,
  shouldShowNotification
} from '@/utils/notificationPreferences';

type ActionCtx = ActionContext<NotificationsState, any>;

export default {
  /**
   * Add a notification (generic)
   */
  add({ commit, state }: ActionCtx, notification: Omit<NotificationData, 'id' | 'timestamp'>): string {
    console.log('[Notifications/add] Adding notification:', notification);

    // Check if notification should be shown based on user preferences
    const isAlarm = isAlarmNotification(notification);
    const bypass = notification.bypassSettings ?? false;

    if (!shouldShowNotification(isAlarm, bypass)) {
      console.log('[Notifications/add] ❌ Notification blocked by user preferences', {
        isAlarm,
        bypass,
        preferences: getNotificationPreferences()
      });
      return ''; // Return empty ID to indicate notification was not shown
    }

    const id = `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const fullNotification: NotificationData = {
      id,
      timestamp: Date.now(),
      duration: notification.duration ?? state.defaultDuration,
      dismissible: notification.dismissible ?? true,
      ...notification,
    };

    console.log('[Notifications/add] Full notification object:', fullNotification);
    console.log('[Notifications/add] Committing to store...');
    commit('addNotification', fullNotification);
    console.log('[Notifications/add] ✅ Committed to store');

    // Auto-dismiss if duration is set
    if (fullNotification.duration && fullNotification.duration > 0) {
      console.log('[Notifications/add] Setting auto-dismiss timer:', fullNotification.duration, 'ms');
      setTimeout(() => {
        console.log('[Notifications/add] Auto-dismissing notification:', id);
        commit('removeNotification', id);
      }, fullNotification.duration);
    } else {
      console.log('[Notifications/add] No auto-dismiss (duration: 0)');
    }

    return id;
  },

  /**
   * Process Firebase Cloud Messaging payload and show notification
   */
  async processFirebaseMessage({ dispatch, rootGetters }: ActionCtx, payload: FCMMessagePayload): Promise<string> {
    console.log('[Notifications] ========================================');
    console.log('[Notifications] Processing FCM message:', payload);

    const { data } = payload;
    console.log('[Notifications] Message data:', data);

    // Determine notification type based on alarm category
    const type = determineNotificationType(data.category, data.title);
    console.log('[Notifications] Determined type:', type);

    // Try to get device name from store
    let deviceName = data.deviceId || 'Unknown Device';
    try {
      const allDevices = rootGetters['catalog/allDevices'] || [];
      console.log('[Notifications] Found devices in store:', allDevices.length);
      const device = allDevices.find((d: any) => d.id === data.deviceId);
      if (device) {
        deviceName = device.name || device.deviceAppName || deviceName;
        console.log('[Notifications] Resolved device name:', deviceName);
      }
    } catch (err) {
      console.warn('[Notifications] Could not resolve device name:', err);
    }

    // Create notification
    const notificationData = {
      type,
      title: data.title,
      body: data.body,
      category: data.category as any,
      deviceId: data.deviceId,
      deviceName,
      registryId: data.registryId,
      alarmId: data.alarmId,
      duration: 8000, // 8 seconds for FCM alerts
      dismissible: true,
    };

    console.log('[Notifications] Creating notification:', notificationData);
    const result = await dispatch('add', notificationData);
    console.log('[Notifications] ✅ Notification created with ID:', result);
    console.log('[Notifications] ========================================');
    return result;
  },

  /**
   * Remove a notification
   */
  remove({ commit }: ActionCtx, notificationId: string): void {
    commit('removeNotification', notificationId);
  },

  /**
   * Clear all notifications
   */
  clearAll({ commit }: ActionCtx): void {
    commit('clearAll');
  },

  /**
   * Show success toast
   */
  async success({ dispatch }: ActionCtx, { title, body, duration }: { title: string; body: string; duration?: number }): Promise<string> {
    return await dispatch('add', {
      type: 'success' as NotificationType,
      title,
      body,
      duration,
    });
  },

  /**
   * Show error toast
   */
  async error({ dispatch }: ActionCtx, { title, body, duration }: { title: string; body: string; duration?: number }): Promise<string> {
    return await dispatch('add', {
      type: 'error' as NotificationType,
      title,
      body,
      duration: duration ?? 0, // Errors don't auto-dismiss by default
    });
  },

  /**
   * Show warning toast
   */
  async warning({ dispatch }: ActionCtx, { title, body, duration }: { title: string; body: string; duration?: number }): Promise<string> {
    return await dispatch('add', {
      type: 'warning' as NotificationType,
      title,
      body,
      duration,
    });
  },

  /**
   * Show info toast
   */
  async info({ dispatch }: ActionCtx, { title, body, duration }: { title: string; body: string; duration?: number }): Promise<string> {
    return await dispatch('add', {
      type: 'info' as NotificationType,
      title,
      body,
      duration,
    });
  },
};

/**
 * Determine notification type based on alarm category and title
 */
function determineNotificationType(category?: string, title?: string): NotificationType {
  if (!category && !title) return 'info';

  const titleLower = (title || '').toLowerCase();
  const categoryLower = (category || '').toLowerCase();

  // Deactivated/resolved alerts = success
  if (titleLower.includes('deactivated') || titleLower.includes('resolved') || titleLower.includes('ended')) {
    return 'success';
  }

  // Critical alarms = error
  if (
    categoryLower.includes('over') ||
    categoryLower.includes('under') ||
    categoryLower.includes('co2') ||
    categoryLower.includes('tvoc') ||
    categoryLower.includes('radar')
  ) {
    return titleLower.includes('deactivated') ? 'success' : 'error';
  }

  // System updates = info
  if (categoryLower.includes('system') || titleLower.includes('system')) {
    return 'info';
  }

  // Default to warning
  return 'warning';
}

